/**
 * Pick an SC charge from the courts' CDR list: type words ("burglary 2nd"), a statute ("16-11-312") or a CDR
 * code. Picking fills the statute and the words; typing your own words still works.
 */
import { useEffect, useRef, useState } from 'react';
import { api } from './api.ts';

export type PickedCharge = { cdr: string; statute: string; description: string; type: string | null; generalSessions: boolean; dv: boolean };

/** The courts write "Burglary / Burglary, 2nd degree" — the part after the slash reads better on paper. */
export const chargeWords = (d: string) => {
  const i = d.indexOf(' / ');
  return i > 0 && i < 30 ? d.slice(i + 3) : d;
};

export function StatutePicker({
  value,
  onText,
  onPick,
  label = 'Charge',
  placeholder = 'Type the charge or statute (e.g. burglary 2nd, 16-11-312)',
  autoFocus,
}: {
  value: string;
  onText: (v: string) => void;
  onPick: (c: PickedCharge) => void;
  label?: string;
  placeholder?: string;
  autoFocus?: boolean;
}) {
  const [hits, setHits] = useState<PickedCharge[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const typed = useRef(false);
  useEffect(() => {
    if (!typed.current) return;
    const q = value.trim();
    if (q.length < 2) return setHits([]);
    const t = setTimeout(() => {
      api
        .get<{ codes: any[] }>(`/api/statutes?q=${encodeURIComponent(q)}`)
        .then((r) => {
          setHits(r.codes.map((c) => ({ cdr: c.code, statute: c.statute, description: c.description, type: c.type, generalSessions: c.generalSessions, dv: c.dv })));
          setActive(0);
          setOpen(true);
        })
        .catch(() => setHits([]));
    }, 180);
    return () => clearTimeout(t);
  }, [value]);
  const pick = (c: PickedCharge) => {
    typed.current = false;
    setOpen(false);
    onPick(c);
  };
  return (
    <div className="sp">
      <input
        value={value}
        autoFocus={autoFocus}
        aria-label={label}
        placeholder={placeholder}
        autoComplete="off"
        onChange={(e) => {
          typed.current = true;
          onText(e.target.value);
        }}
        onFocus={() => hits.length && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (!open || !hits.length) return;
          if (e.key === 'ArrowDown') (e.preventDefault(), setActive((a) => Math.min(hits.length - 1, a + 1)));
          else if (e.key === 'ArrowUp') (e.preventDefault(), setActive((a) => Math.max(0, a - 1)));
          else if (e.key === 'Enter') (e.preventDefault(), pick(hits[active]!));
          else if (e.key === 'Escape') setOpen(false);
        }}
      />
      {open && hits.length > 0 && (
        <ul className="sp-menu" role="listbox" aria-label={`${label}: matching charges`}>
          {hits.map((c, i) => (
            <li key={c.cdr} role="option" aria-selected={i === active} className={i === active ? 'on' : ''} onMouseDown={(e) => (e.preventDefault(), pick(c))}>
              <span className="sp-st mono">{c.statute || '—'}</span>
              <span className="sp-d">{chargeWords(c.description)}</span>
              <span className="sp-tags">
                {c.type === 'F' ? <b className="sp-f">Felony</b> : c.type === 'M' ? <span className="sp-m">Misd.</span> : null}
                {c.dv && <b className="sp-dv">DV</b>}
                <span className="muted small">CDR {c.cdr}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
